<script lang="ts" module>
  /**
   * The glyphs that are Interlock's own: the shell's `Icon` set (`ICON_NAMES`)
   * draws every glyph the owner's games share, and a game adds only what is
   * its own (`packages/shell/src/icons.ts`). These have no shell twin:
   *
   * - `computer`: the chip on "Play the computer";
   * - `pad`: a game controller, on the Controls row in Settings;
   * - `left`, `right`, `hold`, `rotate`, `hardDrop`, `softDrop`: the touch
   *   pads' faces (`screens/TouchControls.svelte`). Drawn rather than typed:
   *   the arrows they used to be are not in Nunito, so a device drew them in
   *   a face of its own;
   * - `diamond`: the handicap's mark beside a score, drawn for the same reason;
   * - `device`: one device passed round, on the Together tab's "On this
   *   device" tile (`docs/decisions/0059`; the shell has no such glyph yet).
   *
   * The same family as the shell's: a 24-unit box, round caps and joins, in
   * `currentColor` (2-unit strokes; 1.8 on a settings badge, as the shell
   * draws its own badge glyphs; the pads' move and soft-drop arrows filled,
   * so a thumb reads them at a glance). Decoration: always `aria-hidden`, so
   * whatever shows one says what it means in words.
   */
  export type GameGlyphName =
    | 'computer'
    | 'pad'
    | 'left'
    | 'right'
    | 'hold'
    | 'rotate'
    | 'hardDrop'
    | 'softDrop'
    | 'diamond'
    | 'device';

  /** The line glyphs: one stroked path each. */
  const STROKES: Partial<Record<GameGlyphName, string>> = {
    computer:
      'M8 5h8a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3ZM9.5 9.5h5v5h-5zM9 2.5V5M15 2.5V5M9 19v2.5M15 19v2.5M2.5 9H5M2.5 15H5M19 9h2.5M19 15h2.5',
    hold: 'M4 9h15M15.5 5.5 19 9l-3.5 3.5M20 15H5M8.5 11.5 5 15l3.5 3.5',
    rotate: 'M20 12a8 8 0 1 1-2.4-5.7M20 4v5h-5',
    hardDrop: 'M12 4v11M7 10l5 5 5-5M5 20h14',
    device:
      'M6 3h6a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2ZM8.5 18h1M17.5 7.5a6 6 0 0 1 0 9M17.5 16.5h3M17.5 16.5v-3',
  };

  /** The filled glyphs: one shape each. */
  const FILLS: Partial<Record<GameGlyphName, string>> = {
    left: 'M16 5 7 12l9 7V5Z',
    right: 'M8 5l9 7-9 7V5Z',
    softDrop: 'M5 8h14l-7 9-7-9Z',
    diamond: 'M12 3l9 9-9 9-9-9 9-9Z',
  };
</script>

<script lang="ts">
  interface Props {
    readonly name: GameGlyphName;
    /** In px; `fill` sizes it to its box instead (a touch pad's face). */
    readonly size?: number | 'fill';
  }
  const { name, size = 24 }: Props = $props();

  const stroke = $derived(STROKES[name]);
  const fill = $derived(FILLS[name]);
</script>

<svg
  class="game-glyph"
  class:game-glyph--fill={size === 'fill'}
  viewBox="0 0 24 24"
  width={size === 'fill' ? undefined : size}
  height={size === 'fill' ? undefined : size}
  aria-hidden="true"
  focusable="false"
>
  {#if name === 'pad'}
    <path
      d="M6 7h12a4 4 0 0 1 4 4v2a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4v-2a4 4 0 0 1 4-4ZM7 10v4M5 12h4M16 11h.01M18.5 13.5h.01"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
    />
  {:else if stroke !== undefined}
    <path
      d={stroke}
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if fill !== undefined}
    <path
      d={fill}
      fill="currentColor"
      stroke="currentColor"
      stroke-width="1"
      stroke-linejoin="round"
    />
  {/if}
</svg>

<style>
  .game-glyph {
    display: block;
    flex: 0 0 auto;
  }

  .game-glyph--fill {
    width: 100%;
    height: 100%;
  }
</style>
